/**
 * Climatiq search results often contain several rows that are identical except
 * for the data year (e.g. the same factor published for 2022, 2023, 2024…). For
 * display we collapse those into a single group so the table shows one row per
 * factor with a "N years available" indicator; using the row imports them all.
 *
 * Rows are grouped by the tuple that uniquely identifies a factor variant
 * IGNORING year: `activity_id | region | source | source_lca_activity | unit_type`.
 * Any other differing dimension (region, data source, LCA boundary, unit type)
 * stays its own group.
 */

/** A displayed row: a representative search result plus every year available for
 *  the otherwise-identical factor (empty for rows that carry no year). */
export interface FactorGroup {
  key: string;
  rep: Record<string, unknown>;
  years: number[];
}

/**
 * Collapse Climatiq result rows that differ ONLY by year into one group each.
 *
 * - The group `key` is `activity_id|region|source|source_lca_activity|unit_type`
 *   (null/undefined parts render as empty string).
 * - `years` accumulates the distinct years across the collapsed rows, sorted
 *   descending. Rows without a year contribute nothing, so a group of only
 *   year-less rows has `years: []`.
 * - `rep` is the row with the latest year (the representative used for display
 *   and estimation); for year-less groups it is simply the first row seen.
 *
 * Pure and order-preserving for distinct groups (insertion order of first sight).
 */
export function groupClimatiqResultsByYear(
  rows: Record<string, unknown>[],
): FactorGroup[] {
  const map = new Map<string, FactorGroup>();
  for (const r of rows) {
    const key = [
      r.activity_id,
      r.region,
      r.source,
      r.source_lca_activity,
      r.unit_type,
    ]
      .map((v) => (v == null ? "" : String(v)))
      .join("|");
    const yr = r.year != null ? Number(r.year) : null;
    const existing = map.get(key);
    if (existing) {
      if (yr != null && !existing.years.includes(yr)) existing.years.push(yr);
      // Keep the latest year's row as the representative.
      if (yr != null && (existing.rep.year == null || yr > Number(existing.rep.year)))
        existing.rep = r;
    } else {
      map.set(key, { key, rep: r, years: yr != null ? [yr] : [] });
    }
  }
  const groups = [...map.values()];
  for (const g of groups) g.years.sort((a, b) => b - a);
  return groups;
}
